import {inject, injectable} from "inversify";
import {LeagueDivision, Matchday} from "@/modules/competitions/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {generateSchedule} from 'sports-schedule-generator';
import {Team} from "@/modules/team/entity";
import {Match} from "@/modules/match/entity";
import {Season} from "@/modules/season/entity";

@injectable()
export default class LeagueDivisionMatchGenerator {
  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {}

  async generate(season: Season, division: LeagueDivision) {
    if (division.finished) {
      throw 'Division is finished already.';
    }

    await division.load(['teams']);

    const seasonStartDate = season.startDate.clone();

    const data = this.configRepository.get('competitions.schedules')
      .find((item) => division.competition instanceof item.competition);

    if (!data) {
      throw 'No schedule data found.';
    }

    const matchdays: Matchday[] = [];

    let prevMatchday;

    for (let i = 1; i <= (division.teams.length - 1) * 2; i++) {
      const isSecondHalf = i >= division.teams.length;
      const currentScheduleRound = (i - 1) % (division.teams.length - 1);
      const slots = data.schedule[currentScheduleRound];

      const week = slots[isSecondHalf ? 1 : 0];
      let scheduledAt = seasonStartDate.add(Math.floor(week / 2), 'week').subtract(2, 'days');
      if (week % 2 === 1) {
        scheduledAt = scheduledAt.add(3, 'days');
      }

      const matchday = new Matchday();
      matchday.season = season;
      matchday.division = division;
      matchday.name = `${i}. matchday`;
      matchday.scheduledAt = scheduledAt.toDate();

      if (prevMatchday) {
        prevMatchday.next = matchday;
        await prevMatchday.save();

        matchday.prev = prevMatchday;
      }

      await matchday.save();

      prevMatchday = matchday;

      matchdays.push((matchday));
    }

    await division.load('matchdays');

    type ScheduleMatch = { home: Team, away: Team }

    await generateSchedule(division.teams).forEachAsync<ScheduleMatch[]>(async (matches, index) => {
      await matches.forEachAsync<ScheduleMatch>(async (match) => {
        const homeMatch = new Match();
        homeMatch.homeTeam = match.home;
        homeMatch.awayTeam = match.away;
        homeMatch.matchday = matchdays[index];
        await homeMatch.save();
        console.debug('LeagueDivisionMatchGenerator::generate Generated home match', homeMatch);

        const awayMatch = new Match();
        awayMatch.homeTeam = match.away;
        awayMatch.awayTeam = match.home;
        awayMatch.matchday = matchdays[index + division.teams.length - 1];
        awayMatch.connectedMatch = homeMatch;
        await awayMatch.save();

        homeMatch.connectedMatch = awayMatch;
        await homeMatch.save();
        console.debug('LeagueDivisionMatchGenerator::generate Generated away match', awayMatch);
      });
    });
  }
}